import React, { useState, useMemo } from 'react';
import {
  DocumentTextIcon,
  FolderIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentIcon,
  CalendarIcon,
  TagIcon,
  CloudArrowUpIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { useDocuments } from '../hooks/useDocuments';
import {
  Document,
  DocumentType,
  getDocumentTypeLabel,
  getDocumentTypeColor,
  formatFileSize,
} from '../api/documents';
import CreateDocumentModal from '../components/documents/CreateDocumentModal';
import EditDocumentModal from '../components/documents/EditDocumentModal';
import ViewDocumentModal from '../components/documents/ViewDocumentModal';
import DeleteConfirmModal from '../components/common/DeleteConfirmModal';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';

const Documents: React.FC = () => {
  const {
    documents,
    statistics,
    loading,
    error,
    createDocument,
    updateDocument,
    deleteDocument,
    refreshData,
  } = useDocuments();

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  // Filter and search states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<DocumentType | ''>('');
  const [showFilters, setShowFilters] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState<'createdAt' | 'title' | 'type' | 'fileSize'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filtered and sorted documents
  const filteredDocuments = useMemo(() => {
    let filtered = documents.filter(doc => {
      const matchesSearch = !searchTerm || 
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = !selectedType || doc.type === selectedType;
      
      return matchesSearch && matchesType;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy];
      let bValue: any = b[sortBy];

      if (sortBy === 'createdAt') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [documents, searchTerm, selectedType, sortBy, sortOrder]);

  const handleCreateDocument = async (data: any) => {
    const result = await createDocument(data);
    if (result) {
      setShowCreateModal(false);
    }
  };

  const handleUpdateDocument = async (data: any) => {
    if (selectedDocument) {
      const result = await updateDocument(selectedDocument.id, data);
      if (result) {
        setShowEditModal(false);
        setSelectedDocument(null);
      }
    }
  };

  const handleDeleteDocument = async () => {
    if (selectedDocument) {
      const result = await deleteDocument(selectedDocument.id);
      if (result) {
        setShowDeleteModal(false);
        setSelectedDocument(null);
      }
    }
  };

  const openEditModal = (document: Document) => {
    setSelectedDocument(document);
    setShowEditModal(true);
  };

  const openViewModal = (document: Document) => {
    setSelectedDocument(document);
    setShowViewModal(true);
  };

  const openDeleteModal = (document: Document) => {
    setSelectedDocument(document);
    setShowDeleteModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading documents</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={refreshData}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <PageHero
        title="Documents"
        subtitle="Manage bird-related documents, certificates, and records"
        actions={
          <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Document
          </button>
        }
      />

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="h-6 w-6 text-slate-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total Documents</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.total || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Recent Uploads</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.recentUploads || 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CloudArrowUpIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Total File Size</dt>
                    <dd className="text-lg font-medium text-slate-900">{formatFileSize(statistics.totalFileSize)}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FolderIcon className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-slate-500 truncate">Document Types</dt>
                    <dd className="text-lg font-medium text-slate-900">{statistics.byType ? Object.keys(statistics.byType).length : 0}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="flex items-center w-full rounded-md border border-gray-300 bg-white focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500">
              <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
              <input
                type="text"
                className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent leading-5 placeholder-gray-500 focus:outline-none focus:ring-0 focus:placeholder-gray-400 sm:text-sm"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="type-filter" className="block text-sm font-medium text-slate-700">
                  Document Type
                </label>
                <select
                  id="type-filter"
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value as DocumentType | '')}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="empty-option-1753461297370" value="">All Types</option>
                  {Object.values(DocumentType).map((type) => (
                    <option key={type} value={type}>
                      {getDocumentTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="sort-by" className="block text-sm font-medium text-slate-700">
                  Sort By
                </label>
                <select
                  id="sort-by"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="createdAt-1753461297370" value="createdAt">Date Created</option>
                  <option key="title-1753461297370" value="title">Title</option>
                  <option key="type-1753461297370" value="type">Type</option>
                  <option key="fileSize-1753461297370" value="fileSize">File Size</option>
                </select>
              </div>

              <div>
                <label htmlFor="sort-order" className="block text-sm font-medium text-slate-700">
                  Sort Order
                </label>
                <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm rounded-md"
                >
                  <option key="desc-1753461297370" value="desc">Newest First</option>
                  <option key="asc-1753461297370" value="asc">Oldest First</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Documents Grid */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-slate-900">
            Documents ({filteredDocuments.length})
          </h3>
        </div>

        {filteredDocuments.length === 0 ? (
          <EmptyState
            className="mt-0 rounded-none border-0 shadow-none bg-transparent"
            title="No documents found"
            description={
              documents.length === 0
                ? 'Get started by creating a new document.'
                : 'Try adjusting your search criteria.'
            }
            icon={<DocumentTextIcon className="h-6 w-6" />}
            action={
              documents.length === 0 ? (
                <button type="button" onClick={() => setShowCreateModal(true)} className="btn btn-primary text-sm focus-pop">
                  <PlusIcon className="h-4 w-4" />
                  Add Document
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {filteredDocuments.map((document) => (
              <div
                key={document.id}
                className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openViewModal(document)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <DocumentTextIcon className="h-5 w-5 text-slate-400 flex-shrink-0" />
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getDocumentTypeColor(document.type)}`}>
                          {getDocumentTypeLabel(document.type)}
                        </span>
                      </div>
                      
                      <h4 className="text-sm font-medium text-slate-900 truncate mb-1">
                        {document.title}
                      </h4>
                      
                      {document.description && (
                        <p className="text-sm text-slate-500 line-clamp-2 mb-2">
                          {document.description}
                        </p>
                      )}

                      <div className="flex items-center text-xs text-slate-500 space-x-4">
                        <div className="flex items-center">
                          <CalendarIcon className="h-3 w-3 mr-1" />
                          {formatDate(document.createdAt)}
                        </div>
                        {document.fileSize && (
                          <div className="flex items-center">
                            <CloudArrowUpIcon className="h-3 w-3 mr-1" />
                            {formatFileSize(document.fileSize)}
                          </div>
                        )}
                      </div>

                      {document.tags && document.tags.length > 0 && (
                        <div className="flex items-center mt-2">
                          <TagIcon className="h-3 w-3 text-slate-400 mr-1" />
                          <div className="flex flex-wrap gap-1">
                            {document.tags.slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 text-xs bg-gray-100 text-slate-600 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                            {document.tags.length > 3 && (
                              <span className="text-xs text-slate-500">
                                +{document.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex-shrink-0 ml-4">
                      <div className="flex space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(document);
                          }}
                          className="text-slate-400 hover:text-slate-600"
                          title="Edit"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(document);
                          }}
                          className="text-slate-400 hover:text-red-600"
                          title="Delete"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateDocumentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateDocument}
      />

      <EditDocumentModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedDocument(null);
        }}
        onSubmit={handleUpdateDocument}
        document={selectedDocument}
      />

      <ViewDocumentModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedDocument(null);
        }}
        document={selectedDocument}
        onEdit={() => {
          setShowViewModal(false);
          setShowEditModal(true);
        }}
        onDelete={() => {
          setShowViewModal(false);
          setShowDeleteModal(true);
        }}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedDocument(null);
        }}
        onConfirm={handleDeleteDocument}
        title="Delete Document"
        message={`Are you sure you want to delete "${selectedDocument?.title}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default Documents; 